/**
 * Clip Panel Component (canvas-v2)
 *
 * 2-column scrollable thumbnail grid showing all reference video and audio clips.
 * Always displays connected reference clips from edges, even if removed from timeline.
 * Clips on the timeline show a checkmark badge; clips not on timeline show "+" and
 * can be clicked to re-add.
 */

import { memo, useMemo } from 'react'
import type { TimelineClip, TimelineAudioClip } from './types'

interface ClipPanelProps {
  /** Video clips currently on the timeline */
  clips: TimelineClip[]

  /** Audio clips currently on the timeline */
  audioClips: TimelineAudioClip[]

  /** All connected reference video clips (from edges) */
  referenceVideoClips?: Array<{ id: string; path: string; name: string }>

  /** All connected reference audio clips (from edges) */
  referenceAudioClips?: Array<{ id: string; path: string; name: string; role?: string }>

  /** Currently selected clip ID */
  selectedClipId: string | null

  /** Callback when a clip is selected */
  onSelectClip: (clipId: string, clipType: 'video' | 'audio') => void

  /** Callback to scroll the timeline to a clip */
  onScrollToClip: (clipId: string, clipType: 'video' | 'audio') => void

  /** Callback to add a reference clip to the timeline */
  onAddClipToTimeline?: (clipId: string, clipType: 'video' | 'audio') => void
}

export const ClipPanel = memo(function ClipPanel({
  clips,
  audioClips,
  referenceVideoClips,
  referenceAudioClips,
  selectedClipId,
  onSelectClip,
  onScrollToClip,
  onAddClipToTimeline,
}: ClipPanelProps) {
  // Map from sourceNodeId to timeline clip for quick lookup
  const timelineClipBySource = useMemo(() => {
    const map = new Map<string, TimelineClip>()
    clips.forEach(c => map.set(c.sourceNodeId, c))
    return map
  }, [clips])

  const timelineAudioBySource = useMemo(() => {
    const map = new Map<string, TimelineAudioClip>()
    audioClips.forEach(c => map.set(c.sourceNodeId, c))
    return map
  }, [audioClips])

  // Use reference clips if available, otherwise fall back to timeline clips
  const videoRefs = referenceVideoClips
  const audioRefs = referenceAudioClips

  // Fall back to legacy behavior if no reference props provided
  const sortedClips = useMemo(
    () => [...clips].sort((a, b) => a.order - b.order),
    [clips]
  )

  const handleClipClick = (clipId: string, clipType: 'video' | 'audio') => {
    onSelectClip(clipId, clipType)
    onScrollToClip(clipId, clipType)
  }

  const hasVideoRefs = videoRefs ? videoRefs.length > 0 : sortedClips.length > 0
  const hasAudioRefs = audioRefs ? audioRefs.length > 0 : audioClips.length > 0

  if (!hasVideoRefs && !hasAudioRefs) {
    return (
      <div className="flex items-center justify-center h-full text-text-muted text-sm p-4">
        No clips connected
      </div>
    )
  }

  return (
    <div className="flex flex-col h-full overflow-y-auto p-2">
      {/* Video clips section */}
      {videoRefs ? (
        videoRefs.length > 0 && (
          <>
            <div className="text-text-secondary text-xs font-medium px-1 py-1.5">
              Video ({videoRefs.length})
            </div>
            <div className="grid grid-cols-2 gap-2">
              {videoRefs.map((ref) => {
                const timelineClip = timelineClipBySource.get(ref.id)
                const isOnTimeline = !!timelineClip
                return (
                  <ClipThumbnail
                    key={ref.id}
                    id={ref.id}
                    label={ref.name}
                    duration={timelineClip?.effectiveDuration ?? 0}
                    thumbnail={timelineClip?.thumbnails[0] || null}
                    isSelected={selectedClipId === (timelineClip?.id ?? ref.id)}
                    speed={timelineClip?.speed ?? 1.0}
                    type="video"
                    isOnTimeline={isOnTimeline}
                    onClick={() => {
                      if (isOnTimeline && timelineClip) {
                        handleClipClick(timelineClip.id, 'video')
                      } else {
                        onAddClipToTimeline?.(ref.id, 'video')
                      }
                    }}
                  />
                )
              })}
            </div>
          </>
        )
      ) : (
        sortedClips.length > 0 && (
          <>
            <div className="text-text-secondary text-xs font-medium px-1 py-1.5">
              Video ({sortedClips.length})
            </div>
            <div className="grid grid-cols-2 gap-2">
              {sortedClips.map((clip) => (
                <ClipThumbnail
                  key={clip.id}
                  id={clip.id}
                  label={clip.label}
                  duration={clip.effectiveDuration}
                  thumbnail={clip.thumbnails[0] || null}
                  isSelected={selectedClipId === clip.id}
                  speed={clip.speed}
                  type="video"
                  isOnTimeline={true}
                  onClick={() => handleClipClick(clip.id, 'video')}
                />
              ))}
            </div>
          </>
        )
      )}

      {/* Audio clips section */}
      {audioRefs ? (
        audioRefs.length > 0 && (
          <>
            <div className="text-text-secondary text-xs font-medium px-1 py-1.5 mt-2">
              Audio ({audioRefs.length})
            </div>
            <div className="grid grid-cols-2 gap-2">
              {audioRefs.map((ref) => {
                const timelineClip = timelineAudioBySource.get(ref.id)
                const isOnTimeline = !!timelineClip
                return (
                  <ClipThumbnail
                    key={ref.id}
                    id={ref.id}
                    label={ref.name}
                    duration={timelineClip?.effectiveDuration ?? 0}
                    thumbnail={null}
                    isSelected={selectedClipId === (timelineClip?.id ?? ref.id)}
                    speed={timelineClip?.speed ?? 1.0}
                    type="audio"
                    muted={timelineClip?.muted}
                    isOnTimeline={isOnTimeline}
                    onClick={() => {
                      if (isOnTimeline && timelineClip) {
                        handleClipClick(timelineClip.id, 'audio')
                      } else {
                        onAddClipToTimeline?.(ref.id, 'audio')
                      }
                    }}
                  />
                )
              })}
            </div>
          </>
        )
      ) : (
        audioClips.length > 0 && (
          <>
            <div className="text-text-secondary text-xs font-medium px-1 py-1.5 mt-2">
              Audio ({audioClips.length})
            </div>
            <div className="grid grid-cols-2 gap-2">
              {audioClips.map((clip) => (
                <ClipThumbnail
                  key={clip.id}
                  id={clip.id}
                  label={clip.sourcePath.split('/').pop() || 'Audio'}
                  duration={clip.effectiveDuration}
                  thumbnail={null}
                  isSelected={selectedClipId === clip.id}
                  speed={clip.speed}
                  type="audio"
                  muted={clip.muted}
                  isOnTimeline={true}
                  onClick={() => handleClipClick(clip.id, 'audio')}
                />
              ))}
            </div>
          </>
        )
      )}
    </div>
  )
})

/** Individual clip thumbnail card */
interface ClipThumbnailProps {
  id: string
  label: string
  duration: number
  thumbnail: string | null
  isSelected: boolean
  speed: number
  type: 'video' | 'audio'
  muted?: boolean
  isOnTimeline?: boolean
  onClick: () => void
}

function ClipThumbnail({
  label,
  duration,
  thumbnail,
  isSelected,
  speed,
  type,
  muted,
  isOnTimeline = true,
  onClick,
}: ClipThumbnailProps) {
  return (
    <button
      className={`
        relative flex flex-col rounded border overflow-hidden text-left transition-colors
        ${isSelected
          ? 'border-blue-400 ring-1 ring-blue-400/50 bg-blue-500/10'
          : isOnTimeline
            ? 'border-border bg-card hover:bg-card-hover'
            : 'border-border/50 bg-card/50 hover:bg-card-hover/70'
        }
      `}
      onClick={onClick}
    >
      {/* Thumbnail area */}
      <div className="relative w-full aspect-video bg-black/40 flex items-center justify-center">
        {thumbnail ? (
          <img src={thumbnail} alt={label} className="w-full h-full object-cover" />
        ) : (
          <div className={`text-text-muted ${isOnTimeline ? 'text-lg' : 'text-lg opacity-50'}`}>
            {type === 'video' ? (
              <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
            ) : (
              <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3" />
              </svg>
            )}
          </div>
        )}

        {/* Timeline status badge */}
        <div className="absolute top-1 left-1 flex gap-1">
          {isOnTimeline ? (
            <span className="w-4 h-4 rounded-full bg-teal-500/80 flex items-center justify-center">
              <svg className="w-2.5 h-2.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
            </span>
          ) : (
            <span className="w-4 h-4 rounded-full bg-white/20 flex items-center justify-center text-white/60 text-xs font-bold">
              +
            </span>
          )}
        </div>

        {/* Speed/mute badges */}
        <div className="absolute top-1 right-1 flex gap-1">
          {speed !== 1.0 && (
            <span className="text-xxs font-medium px-1 py-0.5 rounded bg-amber-500/80 text-white">
              {speed}x
            </span>
          )}
          {muted && (
            <span className="text-xxs px-1 py-0.5 rounded bg-gray-500/60 text-gray-300">
              M
            </span>
          )}
        </div>
      </div>

      {/* Info */}
      <div className="px-1.5 py-1">
        <div className="text-foreground text-xs truncate">{label}</div>
        <div className="text-text-muted text-xxs">
          {isOnTimeline ? formatDuration(duration) : 'Not on timeline'}
        </div>
      </div>
    </button>
  )
}

function formatDuration(seconds: number): string {
  if (seconds < 60) {
    return `${seconds.toFixed(1)}s`
  }
  const mins = Math.floor(seconds / 60)
  const secs = Math.floor(seconds % 60)
  return `${mins}:${secs.toString().padStart(2, '0')}`
}
